import { Box, Paper, Stack } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ComponentType, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import {
    BodyContourIcon,
    BoolIcon,
    CouchIcon,
    MarginIcon,
    RingIcon,
} from '~/assets/workspace-icons';
import PanelHeader from '~/components/toolbar/shared/PanelHeader';
import { WINDOW_MARGIN_PX } from '~/components/toolbar/shared/floatingPanelPlacement';
import {
    type PostProcessingTab,
    type OperationPanelState,
    operationPanelState,
} from '~/state/atoms/toolsAtoms';
import BooleanOperationContent from './boolean/BooleanOperationContent';
import CouchOperationContent from './couch/CouchOperationContent';
import { POST_PROCESSING_TAB_META } from './postProcessing/postProcessingTabs';
import PostProcessingOperationContent from './postProcessing/PostProcessingOperationContent';
import MarginOperationContent from './margin/MarginOperationContent';
import RingOperationContent from './ring/RingOperationContent';
import ThresholdSegmentationOperationContent from './thresholdSegmentation/ThresholdSegmentationOperationContent';

/**
 * Width in px of every operation panel. 400 keeps a boolean step row
 * (letter, operation, structure, remove) on one line with the structure
 * name readable. The placement beside the toolbar keeps this much room.
 */
export const OPERATION_PANEL_WIDTH = 400;

interface UnifiedOperationPanelProps {
    panelState: OperationPanelState;
    onClose: () => void;
    position: { x: number; y: number } | null;
}

/**
 * What the header says and shows per operation. Titles carry their own
 * message ids rather than reusing the toolbar tooltip ids:
 * `toolbar.booleanOperation.tooltip` is plural ("Boolean Operations")
 * while this header reads "Boolean Operation" singular, and the couch
 * panel names the activity ("Insert couch structures") where its toolbar
 * tooltip names the tool. Post Processing names the OPERATION, not the
 * panel, through its tab's own meta: which operation you are in is the
 * one thing the header can say that nothing else does. The record is
 * keyed on every panel type, so a new panel without a title here is a
 * compile error, not a blank header over a CT.
 */
const OPERATION_PANEL_META: Record<
    Exclude<OperationPanelState['type'], 'closed' | 'post-processing'>,
    { titleId: string; Icon: ComponentType }
> = {
    'boolean-operation': {
        titleId: 'operationPanel.title.booleanOperation',
        Icon: BoolIcon,
    },
    margin: { titleId: 'operationPanel.title.margin', Icon: MarginIcon },
    ring: { titleId: 'operationPanel.title.ring', Icon: RingIcon },
    'threshold-segmentation': {
        titleId: 'operationPanel.title.thresholdSegmentation',
        Icon: BodyContourIcon,
    },
    couch: { titleId: 'operationPanel.title.couch', Icon: CouchIcon },
};

function getPanelMeta(
    panelState: OperationPanelState
): { titleId: string; Icon: ComponentType } | null {
    switch (panelState.type) {
        case 'closed':
            return null;
        case 'post-processing': {
            const { labelId, Icon } = POST_PROCESSING_TAB_META[panelState.tab];
            return { titleId: labelId, Icon };
        }
        default:
            return OPERATION_PANEL_META[panelState.type];
    }
}

export default function UnifiedOperationPanel({
    panelState,
    onClose,
    position,
}: UnifiedOperationPanelProps) {
    const intl = useIntl();
    const [closeDisabled, setCloseDisabled] = useState(false);
    const setPanelState = useSetRecoilState(operationPanelState);

    const handlePostProcessingTabChange = useCallback(
        (tab: PostProcessingTab) => {
            // The same hold that keeps the panel open during a commit: a
            // tab change unmounts the tab that is writing, and its cleanup
            // would release the hold, letting the panel close out from
            // under the write.
            if (closeDisabled) {
                return;
            }
            setPanelState({ type: 'post-processing', tab });
        },
        [closeDisabled, setPanelState]
    );

    const { attributes, listeners, setNodeRef, transform, isDragging } =
        useDraggable({ id: 'operations-panel' });

    const dragTransform = transform
        ? CSS.Translate.toString(transform)
        : undefined;

    // Escape key handler. An Escape something else already handled, such
    // as a tool cancelling its action in progress, leaves the panel open.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !e.defaultPrevented && !closeDisabled) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [closeDisabled, onClose]);

    const handleClose = useCallback(() => {
        if (!closeDisabled) {
            onClose();
        }
    }, [closeDisabled, onClose]);

    if (!position) {
        return null;
    }

    const meta = getPanelMeta(panelState);
    const title = meta ? intl.formatMessage({ id: meta.titleId }) : '';

    return (
        <Paper
            ref={setNodeRef}
            elevation={8}
            sx={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                width: OPERATION_PANEL_WIDTH,
                // Ends a window margin above the bottom edge and the body
                // scrolls, so the committing action stays reachable.
                maxHeight: `calc(100vh - ${position.y + WINDOW_MARGIN_PX}px)`,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1400,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.raised',
                backgroundImage: 'none',
                overflow: 'hidden',
                transform: isDragging ? dragTransform : undefined,
                cursor: isDragging ? 'grabbing' : undefined,
            }}
            onClick={(e) => e.stopPropagation()}
            {...attributes}
        >
            {/* The header rule runs edge to edge, so the header carries
                its own padding and the body its own. */}
            <Box
                sx={{
                    pl: 2,
                    pr: 1.5,
                    py: 1.5,
                    flexShrink: 0,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <PanelHeader
                    title={title}
                    icon={meta && <meta.Icon />}
                    dragListeners={listeners}
                    onClose={handleClose}
                    closeDisabled={closeDisabled}
                    divider={false}
                />
            </Box>
            <Box sx={{ p: 2, minHeight: 0, overflowY: 'auto' }}>
                <Stack spacing={2}>
                    {panelState.type === 'boolean-operation' && (
                        <BooleanOperationContent onClose={onClose} />
                    )}

                    {panelState.type === 'margin' && (
                        <MarginOperationContent
                            mode={panelState.mode}
                            onClose={onClose}
                            onPreventCloseChange={setCloseDisabled}
                        />
                    )}

                    {panelState.type === 'ring' && (
                        <RingOperationContent
                            onClose={onClose}
                            onPreventCloseChange={setCloseDisabled}
                        />
                    )}

                    {panelState.type === 'threshold-segmentation' && (
                        <ThresholdSegmentationOperationContent
                            onClose={onClose}
                            onPreventCloseChange={setCloseDisabled}
                        />
                    )}

                    {panelState.type === 'couch' && (
                        <CouchOperationContent
                            onClose={onClose}
                            onPreventCloseChange={setCloseDisabled}
                        />
                    )}

                    {panelState.type === 'post-processing' && (
                        <PostProcessingOperationContent
                            tab={panelState.tab}
                            onTabChange={handlePostProcessingTabChange}
                            onPreventCloseChange={setCloseDisabled}
                            tabsDisabled={closeDisabled}
                        />
                    )}
                </Stack>
            </Box>
        </Paper>
    );
}
